// StoreOptionValuesEditor — the `values` field of a StoreOptionSet (and the
// inline values of a product's option, aldoo-ra/Admin/Store/extensions/options):
// a sortable list of values, each { key, label, hex, image, order }.
//
// `label` is written per language ({ en: "Medium", bg: "Среден" }): the
// primary language inline, the others under the row's language button. `hex`
// is picked with the ColorPicker editor, `image` with the image field editor
// (the media library). `order` is the row's place; dragging a row by its
// handle (or the arrows) reorders the list.
import React, { useEffect, useState } from "react"
import { Bars3Icon, ChevronDownIcon, ChevronUpIcon, LanguageIcon, TrashIcon, PlusIcon, XMarkIcon } from "@heroicons/react/24/outline"
import Input from "aldoo-ra/Input"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import { GetSupportedLanguages } from "aldoo-ra/Localization/api"
import ColorPickerEditor from "./color-picker"
import ImageEditor from "./image"

const FALLBACK_LANGUAGES = [{ code: "en", displayName: "English" }]
let cachedLanguages = null

/** The site's languages, primary first (the Localization admin's). */
export function useStoreLanguages() {
  const [languages, setLanguages] = useState(cachedLanguages || FALLBACK_LANGUAGES)
  useEffect(() => {
    if (cachedLanguages) return
    let alive = true
    GetSupportedLanguages()
      .then((res) => {
        const list = (Array.isArray(res) ? res : res?.languages || []).filter((l) => l?.code)
        if (list.length) cachedLanguages = list
        if (alive) setLanguages(cachedLanguages || FALLBACK_LANGUAGES)
      })
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [])
  return languages
}

const isRecord = (value) => !!value && typeof value === "object" && !Array.isArray(value)

/** A label in a language: a plain string is every language's; a map falls back to en, then to any. */
export const labelIn = (label, code) => {
  if (typeof label === "string") return label
  if (!isRecord(label)) return ""
  return label[code] || label.en || Object.values(label).find((text) => typeof text === "string" && text) || ""
}

/** The label with one language's text set (a plain string becomes the primary language's). */
const withLabel = (label, code, text, primary) => {
  const map = typeof label === "string" ? (label ? { [primary]: label } : {}) : { ...(isRecord(label) ? label : {}) }
  if (text) map[code] = text
  else delete map[code]
  return map
}

const pathOf = (entry) => {
  const path = isRecord(entry) ? entry.url || entry.path : entry
  return typeof path === "string" && path && !path.startsWith("blob:") ? path : ""
}

/**
 * The image field editor over a plain path (or a list of paths): it keeps
 * its own value while an upload runs (a blob URL is not a path yet) and
 * hands on the paths once they are.
 */
export function ImageField({ value, onChange, multiple = false, readOnly = false }) {
  const paths = (multiple ? (Array.isArray(value) ? value : []) : [value]).filter((path) => typeof path === "string" && path)
  const asEntries = () => (multiple ? paths.map((url) => ({ url })) : paths[0] ? { url: paths[0] } : null)
  const [raw, setRaw] = useState(asEntries)
  const settled = (entries) => (Array.isArray(entries) ? entries : entries ? [entries] : []).map(pathOf).filter(Boolean)

  // a value changed from outside (a bulk action, another row) is taken as it is
  useEffect(() => {
    if (JSON.stringify(settled(raw)) === JSON.stringify(paths)) return
    setRaw(asEntries())
  }, [JSON.stringify(paths)])

  const field = { id: "image", label: "", multiple, formats: ["png", "jpg", "jpeg", "webp", "svg"] }
  return (
    <ImageEditor
      field={field}
      value={raw}
      showInfo={false}
      readOnly={readOnly}
      onChange={(next) => {
        setRaw(next)
        const done = settled(next)
        // still uploading: the path comes with the next change
        const pending = (Array.isArray(next) ? next : next ? [next] : []).some((entry) => isRecord(entry) && String(entry.url || "").startsWith("blob:"))
        if (pending) return
        onChange?.(multiple ? done : done[0] || "")
      }}
    />
  )
}

/** A value list as the editor keeps it: records, `order` = their place. */
const asRows = (value) => (Array.isArray(value) ? value.filter(isRecord) : [])
const ordered = (rows) => rows.map((row, index) => ({ ...row, order: index }))

/**
 * The list itself, usable outside the CMS (a product's own option values).
 * `onChange(values)` gets the whole list, renumbered.
 */
// `language`: the one language the labels are edited in (the product dialog's, picked at its
// top) — then there is no other-languages row; `base`: the language a plain-text label is in
export function OptionValuesList({ value, onChange, readOnly = false, showImages = true, showColors = true, language = null, base = null }) {
  const languages = useStoreLanguages()
  const primary = base || languages[0]?.code || "en"
  const editIn = language || primary
  const rows = asRows(value)
  const [open, setOpen] = useState({})
  const [dragging, setDragging] = useState(null)

  const commit = (next) => onChange?.(ordered(next))
  const update = (index, patch) => commit(rows.map((row, at) => (at === index ? { ...row, ...patch } : row)))
  const remove = (index) => commit(rows.filter((_, at) => at !== index))
  const move = (from, to) => {
    if (to < 0 || to >= rows.length || from === to) return
    const next = [...rows]
    const [row] = next.splice(from, 1)
    next.splice(to, 0, row)
    commit(next)
  }
  const add = () => commit([...rows, { key: "", label: {}, order: rows.length }])
  const duplicates = new Set(rows.map((row) => String(row.key || "").trim()).filter((key, index, all) => key && all.indexOf(key) !== index))

  return (
    <div className="flex flex-col gap-2 w-full">
      {!rows.length && (
        <Typography variant="admin_body2" className="opacity-70">
          No values yet.
        </Typography>
      )}
      {rows.map((row, index) => {
        const key = String(row.key || "")
        const others = language ? [] : languages.slice(1)
        return (
          <div
            key={index}
            className={`flex flex-col gap-2 rounded-lg border p-2 ${dragging === index ? "border-admin_primary" : "border-admin_bordered"}`}
            onDragOver={(e) => dragging !== null && e.preventDefault()}
            onDrop={() => {
              if (dragging !== null) move(dragging, index)
              setDragging(null)
            }}
          >
            <div className="flex flex-wrap items-center gap-2">
              {!readOnly && (
                // the handle drags the row: the inputs keep their own text selection
                <span draggable title="Drag to reorder" className="cursor-move shrink-0" onDragStart={() => setDragging(index)} onDragEnd={() => setDragging(null)}>
                  <Bars3Icon className="w-5 h-5 text-admin_text_secondary" />
                </span>
              )}
              <div className="w-28 shrink-0">
                <Input
                  variant="admin"
                  value={key}
                  placeholder="Key"
                  readOnly={readOnly}
                  error={!key.trim() ? "Required" : duplicates.has(key.trim()) ? "Duplicate" : ""}
                  onChange={(e) => update(index, { key: e.target.value })}
                />
              </div>
              <div className="flex-1 min-w-[160px]">
                <Input
                  variant="admin"
                  value={typeof row.label === "string" ? (editIn === primary ? row.label : "") : row.label?.[editIn] || ""}
                  placeholder={(editIn !== primary && labelIn(row.label, primary)) || `Label (${editIn})`}
                  readOnly={readOnly}
                  onChange={(e) => update(index, { label: withLabel(row.label, editIn, e.target.value, primary) })}
                />
              </div>
              {others.length > 0 && (
                <Button variant="admin_icon" title="Other languages" onClick={() => setOpen((state) => ({ ...state, [index]: !state[index] }))}>
                  <LanguageIcon className={`w-5 h-5 ${open[index] ? "text-admin_primary" : ""}`} />
                </Button>
              )}
              {showColors && (
                <div className="flex items-center gap-1">
                  <ColorPickerEditor value={row.hex || ""} readOnly={readOnly} onChange={(hex) => update(index, { hex })} />
                  {row.hex && !readOnly && (
                    <Button variant="admin_icon" title="No color" onClick={() => update(index, { hex: "" })}>
                      <XMarkIcon className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              )}
              {!readOnly && (
                <div className="flex items-center ml-auto">
                  <Button variant="admin_icon" title="Up" disabled={index === 0} onClick={() => move(index, index - 1)}>
                    <ChevronUpIcon className="w-4 h-4" />
                  </Button>
                  <Button variant="admin_icon" title="Down" disabled={index === rows.length - 1} onClick={() => move(index, index + 1)}>
                    <ChevronDownIcon className="w-4 h-4" />
                  </Button>
                  <Button variant="admin_icon" title="Remove" onClick={() => remove(index)}>
                    <TrashIcon className="w-4 h-4 text-admin_error" />
                  </Button>
                </div>
              )}
            </div>
            {open[index] && others.length > 0 && (
              <div className="flex flex-wrap gap-2 pl-7">
                {others.map((language) => (
                  <div key={language.code} className="w-48">
                    <Input
                      variant="admin"
                      value={isRecord(row.label) ? row.label[language.code] || "" : ""}
                      placeholder={`${language.displayName || language.code}`}
                      readOnly={readOnly}
                      onChange={(e) => update(index, { label: withLabel(row.label, language.code, e.target.value, primary) })}
                    />
                  </div>
                ))}
              </div>
            )}
            {showImages && (
              <div className="pl-7">
                <ImageField value={row.image || ""} readOnly={readOnly} onChange={(image) => update(index, { image })} />
              </div>
            )}
          </div>
        )
      })}
      {!readOnly && (
        <div>
          <Button variant="admin_secondary" onClick={add}>
            <PlusIcon className="w-4 h-4" /> Add value
          </Button>
        </div>
      )}
    </div>
  )
}

export default function StoreOptionValuesEditor({ value, onChange, readOnly = false }) {
  return <OptionValuesList value={value} onChange={onChange} readOnly={readOnly} />
}
